import { z } from 'zod';

/**
 * The shapes the four reference screens read and write.
 *
 * Types come from `z.infer`, never hand-written, so the parse and the type
 * cannot disagree.
 *
 * **Every field but `id` is optional on a row schema.** `?fields=` projection
 * is real: a list that hides a column does not receive it, and a required
 * field behind a hidden column fails the whole parse — which blanks the table
 * rather than the column.
 */

const auditFields = {
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  version: z.number().optional(),
  isActive: z.boolean().optional(),
};

export const currencyRowSchema = z.object({
  id: z.string(),
  code: z.string().optional(),
  name: z.string().optional(),
  symbol: z.string().nullable().optional(),
  minorUnit: z.number().optional(),
  numericCode: z.string().nullable().optional(),
  ...auditFields,
});

export const countryRowSchema = z.object({
  id: z.string(),
  code: z.string().optional(),
  alpha3: z.string().nullable().optional(),
  numericCode: z.string().nullable().optional(),
  name: z.string().optional(),
  phonePrefix: z.string().nullable().optional(),
  ...auditFields,
});

export const stateRowSchema = z.object({
  id: z.string(),
  code: z.string().optional(),
  name: z.string().optional(),
  countryCode: z.string().optional(),
  countryName: z.string().optional(),
  ...auditFields,
});

export const cityRowSchema = z.object({
  id: z.string(),
  code: z.string().nullable().optional(),
  name: z.string().optional(),
  stateCode: z.string().optional(),
  stateName: z.string().optional(),
  countryCode: z.string().optional(),
  ...auditFields,
});

export const importReportSchema = z.object({
  importId: z.string(),
  willCreate: z.number(),
  willUpdate: z.number(),
  unchanged: z.number(),
  errors: z.array(
    z.object({
      line: z.number(),
      field: z.string(),
      value: z.string(),
      message: z.string(),
    }),
  ),
});

export const importResultSchema = z.object({ created: z.number(), updated: z.number() });

export type CurrencyRow = z.infer<typeof currencyRowSchema>;
export type CountryRow = z.infer<typeof countryRowSchema>;
export type StateRow = z.infer<typeof stateRowSchema>;
export type CityRow = z.infer<typeof cityRowSchema>;
export type ImportReport = z.infer<typeof importReportSchema>;
export type ImportResult = z.infer<typeof importResultSchema>;
